import { DetectorEnfasis } from './gestos';
import { clamp, Pulso, RuidoSuave, VIDA_DEFECTOS, type Rng, type VidaOpts } from './vida';

/**
 * Modulación lenta de la expresión (tanda 8, pieza 3).
 *
 * Una expresión sostenida durante una frase entera se ve como una foto
 * pegada sobre la cara: solo se mueve la boca. Una cara real respira la
 * emoción, con variaciones chicas de intensidad. Esta capa da un factor
 * que el adapter aplica a los pesos **de la expresión** —no a la idle, ni
 * al habla, ni a los gestos—:
 *
 * ```
 * f(t) = 1 + a · ruido(t) + b · enfasis(t)
 * ```
 *
 * recortado a `[modulacionMin, modulacionMax]`. El ruido es lento
 * (~0,25 Hz) y está siempre; el énfasis sale de su propio
 * `DetectorEnfasis` sobre el nivel de la voz, así que solo actúa mientras
 * el paciente habla.
 *
 * El factor multiplica, así que en `NEUTRAL` —pesos de expresión en
 * cero— no tiene efecto. Y no mira qué expresión está aplicada: es el
 * mismo factor para cualquiera.
 */
export class CapaModulacion {
  private vida: Readonly<VidaOpts>;
  private readonly ruido: RuidoSuave;
  private readonly detector: DetectorEnfasis;
  private readonly enfasis = new Pulso();
  /** Último factor calculado. */
  factor = 1;

  constructor(opts: { rng?: Rng; vida?: Readonly<VidaOpts> } = {}) {
    this.vida = opts.vida ?? VIDA_DEFECTOS;
    this.ruido = new RuidoSuave(opts.rng ?? Math.random);
    this.detector = new DetectorEnfasis(this.vida);
  }

  configurar(vida: Readonly<VidaOpts>): void {
    this.vida = vida;
    this.detector.configurar(vida);
  }

  /**
   * @param nivel nivel suavizado de la voz (`HablaOffsets.nivel`).
   * @param hablando si hay un audio sonando.
   * @param umbralPausa el umbral de pausa de la capa de habla.
   */
  update(deltaMs: number, nivel: number, hablando: boolean, umbralPausa: number): number {
    const v = this.vida;
    if (this.detector.update(deltaMs, hablando ? nivel : 0, umbralPausa)) this.enfasis.disparar(1);
    const e = this.enfasis.update(deltaMs, v.modulacionEnfasisAtaqueMs, v.modulacionEnfasisCaidaMs);
    const r = this.ruido.update(deltaMs, v.modulacionHz);
    this.factor = clamp(
      1 + v.modulacionRuido * r + v.modulacionEnfasis * e,
      v.modulacionMin,
      v.modulacionMax,
    );
    return this.factor;
  }

  /** Vuelve al factor 1 de inmediato. Para las capturas. */
  reiniciar(): void {
    this.enfasis.reiniciar();
    this.detector.reiniciar();
    this.factor = 1;
  }
}
